/* @flow */
import * as React from 'react';
import { computed, observable, autorun, reaction } from 'mobx';
import { Link, withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';

import NotificationTableItem from 'components/NotificationTableItem/NotificationTableItem';
import Loading from 'components/Loading/Loading';

import NotificationStore, { Subscription } from 'stores/NotificationStore';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import styles from './Notifications.module.scss';

type InjectedProps = {
  notificationStore: NotificationStore,
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('notificationStore', 'dashboardStore')
@withRouter
@observer
export default class Notifications extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  reactionDisposers: Function[] = [];

  @observable firstLoad = true;
  @observable notificationsSubscription: ?Subscription;

  async componentDidMount() {
    const { notificationStore, dashboardStore } = this.props;

    if (!this.notificationsSubscription) {
      this.reactionDisposers.push(
        reaction(
          () => dashboardStore.loadedTradingAccount,
          tradingAccount => {
            if (this.notificationsSubscription) {
              notificationStore.unsubscribeFromNotifications(this.notificationsSubscription.id);
              this.notificationsSubscription = undefined;
            }

            this.notificationsSubscription = notificationStore.subscribeToNotifications(
              {
                type: 'notifications',
                urlParams: [],
                searchParams: [
                  ...(tradingAccount !== 0
                    ? [{ key: 'account_id', values: [tradingAccount] }]
                    : []),
                ],
              },
              { background: false }
            );
          },
          { fireImmediately: true }
        )
      );

      this.firstLoad = false;
    }
  }

  async componentWillUnmount() {
    const { notificationStore, match } = this.props;

    if (!match.path.includes('/welcome-page')) {
      if (this.notificationsSubscription) {
        notificationStore.unsubscribeFromNotifications(this.notificationsSubscription.id);
        this.notificationsSubscription = undefined;
      }

      for (const disposer of this.reactionDisposers) {
        disposer();
      }

      this.reactionDisposers = [];
    }
  }

  @computed get notifications(): React.Element<*>[] {
    return this.notificationsSubscription
      ? this.notificationsSubscription.data.map(this.renderNotification)
      : [];
  }

  renderNotification = (n: Object) => (
    <NotificationTableItem
      key={n.event_id}
      {...{ notification: n, store: this.props.notificationStore }}
    />
  );

  renderCardContent = () => {
    const { t } = this.props;

    if (
      this.firstLoad &&
      (!this.notificationsSubscription || this.notificationsSubscription.loading)
    ) {
      return <Loading />;
    }

    if (this.notifications.length) {
      return <List disablePadding={true}>{this.notifications}</List>;
    }

    return (
      <div className={styles['no-data']}>
        {t('general:dashboards.messages.no_notifications')}
        <small>{t('general:dashboards.messages.listening_for_notifications')}</small>
        {this.notificationsSubscription && this.notificationsSubscription.loading ? (
          <Loading variant="small" />
        ) : (
          <div className={styles.liveIndicatorWrapper}>
            <div className={styles.liveIndicator} />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { t } = this.props;

    return (
      <Card className={styles['d-widget']}>
        <CardHeader
          className={styles['d-widget-header']}
          title={<Typography variant="headline">{t('general:notification_plural')}</Typography>}
          action={
            <Link to={{ pathname: '/notifications', state: { tab: 1 } }}>
              <Button variant="contained" color="primary" style={{ marginRight: '12px' }}>
                {t('general:actions.view_notification_plural')}
              </Button>
            </Link>
          }
        />
        <Divider />
        <CardContent className={styles['d-widget-content']}>{this.renderCardContent()}</CardContent>
      </Card>
    );
  }
}
